import { unionBy } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';

import type { Scalars } from '../../__generated-gql-types__/globalTypes';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { OrganizationImg, OrganizationType } from '../common/OrganizationImg';
import {
  MultiTypeahead,
  type MultiTypeaheadProps,
  SingleTypeahead,
  type SingleTypeaheadProps,
} from '../inputs/Typeahead';
import { type SelectOptionType } from '../inputs/selects';

import {
  type SchoolTypeaheadSchoolFragment,
  useSchoolTypeaheadQuery,
} from './__generated-gql-types__/SchoolTypeahead.generated';

export type SchoolTypeaheadSchool = SchoolTypeaheadSchoolFragment;

const isSchoolTypeaheadSchoolEqual = (
  s1: SchoolTypeaheadSchool,
  s2: SchoolTypeaheadSchool,
) => s1.id === s2.id;

interface CommonSchoolTypeaheadProps {
  categoryUrn?: Scalars['CategoryUrn']['output'];
}

type SingleSchoolTypeaheadProps = Omit<
  SingleTypeaheadProps<SchoolTypeaheadSchool>,
  'options' | 'onInputChange'
> &
  CommonSchoolTypeaheadProps;

export const SingleSchoolTypeahead: React.FC<SingleSchoolTypeaheadProps> = ({
  value,
  placeholder = 'Search schools...',
  categoryUrn,
  ...props
}) => {
  const queryProps = useSchoolTypeaheadQueryData({ value }, categoryUrn);

  return (
    <SingleTypeahead
      {...props}
      value={value}
      isValueEqual={isSchoolTypeaheadSchoolEqual}
      placeholder={placeholder}
      renderOption={renderSchoolOption}
      {...queryProps}
    />
  );
};

type MultiSchoolTypeaheadProps = Omit<
  MultiTypeaheadProps<SchoolTypeaheadSchool>,
  'options' | 'onInputChange'
> &
  CommonSchoolTypeaheadProps;

export const MultiSchoolTypeahead: React.FC<MultiSchoolTypeaheadProps> = ({
  values,
  placeholder = 'Search schools...',
  categoryUrn,
  ...props
}) => {
  const queryProps = useSchoolTypeaheadQueryData({ values }, categoryUrn);

  return (
    <MultiTypeahead
      {...props}
      values={values}
      isValueEqual={isSchoolTypeaheadSchoolEqual}
      placeholder={placeholder}
      renderOption={renderSchoolOption}
      {...queryProps}
    />
  );
};

const schoolToSelectOptionType = (
  school: SchoolTypeaheadSchool,
): SelectOptionType<SchoolTypeaheadSchool> => ({
  value: school,
  label: school.name,
});

const useSchoolTypeaheadQueryData = (
  {
    value,
    values,
  }: {
    value?: Nullable<SchoolTypeaheadSchool>;
    values?: SchoolTypeaheadSchool[];
  },
  categoryUrn?: Scalars['CategoryUrn']['output'],
) => {
  const [rawQuery, setRawQuery] = useState<string>('');
  const query = useDebounce(rawQuery, 500);
  const onInputChange = useCallback((input: string) => setRawQuery(input), []);

  const { data, loading } = useSchoolTypeaheadQuery({
    variables: { query, categoryUrn },
  });

  const options: Array<SelectOptionType<SchoolTypeaheadSchool>> =
    useMemo(() => {
      const schools = unionBy(
        data?.schoolTypeahead ?? [],
        value ? [value] : values,
        'id',
      );
      return schools.map(schoolToSelectOptionType);
    }, [data?.schoolTypeahead, value, values]);

  return { onInputChange, options, loading };
};

const renderSchoolOption = (school: SchoolTypeaheadSchool) => (
  <div className="flex items-center">
    <OrganizationImg
      nonSizeClassName="mr-2 rounded"
      pictureLink={school.logoLink}
      name={school.name}
      type={OrganizationType.SCHOOL}
    />
    {school.name}
  </div>
);
